import { z } from 'zod';
import { AllowlistRange, IpAddress, IsoTime, ServerId } from './basics.js';
import { BotCatalog, BotId, BotVerificationResult, TrafficCategory } from './bots.js';
import { parseAddress } from './cidr.js';
import { GeoInfo } from './country.js';
import { ServerGroupId } from './server.js';
import { WebTrafficSettings } from './settings.js';
import { WebEnvironmentSetting } from './web.js';
/**
 * The traffic batch (knowledge doc sections 8.7 and 15; skill `web-traffic.md`
 * §9; P6.3): what the agent's `web_traffic` module sends every minute instead of
 * its log lines — which never leave the server (rule 19).
 *
 * Top lists per dimension, each capped, so a minute of attack traffic is the same
 * size on the wire and in the database as a quiet one. Every key is printable
 * ASCII and bounded, because every key came from a request somebody else wrote.
 */
export const TrafficDimension = z.enum([
    'total',
    'category',
    'bot',
    'ua_family',
    'ip',
    'path',
    'status',
    'host',
    // P11.7: one site's own top lists, each row naming its `host`.
    'site_category',
    'site_bot',
    'site_ip',
    'site_path',
    'site_status',
    // P11.13: two lists joined, so "who asked for this page" has an answer.
    'ip_path',
    'path_status',
    'ip_status',
    'bot_path',
    'bot_status',
    'ip_bot',
]);
/** Skill §9's caps, and `category` and `status`, which are small by nature. */
export const TRAFFIC_CAPS = {
    total: 1,
    category: TrafficCategory.options.length,
    bot: 50,
    ua_family: 30,
    ip: 50,
    path: 50,
    status: 5,
    host: 20,
    // Per site, for the busiest `TRAFFIC_SITES` sites of the minute.
    site_category: TrafficCategory.options.length,
    site_bot: 10,
    site_ip: 15,
    site_path: 15,
    site_status: 5,
    // Pairs: the heaviest combinations only. A scanner that asks one page a hundred
    // times is in them; an address that touched two hundred pages once each is not.
    ip_path: 100,
    path_status: 50,
    ip_status: 50,
    bot_path: 50,
    bot_status: 30,
    ip_bot: 50,
};
/** The sites a minute breaks down, the busiest first (P11.7). */
export const TRAFFIC_SITES = 20;
/** The site dimensions, and the plain dimension each one breaks down. */
export const SITE_DIMENSIONS = {
    site_category: 'category',
    site_bot: 'bot',
    site_ip: 'ip',
    site_path: 'path',
    site_status: 'status',
};
/**
 * The pair dimensions (P11.13) and what each joins. A row's key is the two parts
 * with a space between: the part that can hold a space — a path — is always the
 * one the split leaves whole (see `splitPairKey`). The status in a pair is the
 * exact code (`404`), not the class the `status` list counts in: a 403 and a 404
 * mean different things to the person deciding what to block.
 *
 * Still aggregates, capped, never a log line (rule 19). The ones with an address in
 * them are kept as long as addresses are (14 days, minute rows only).
 */
export const PAIR_DIMENSIONS = {
    ip_path: ['ip', 'path'],
    path_status: ['path', 'status'],
    ip_status: ['ip', 'status'],
    bot_path: ['bot', 'path'],
    bot_status: ['bot', 'status'],
    ip_bot: ['ip', 'bot'],
};
export const isPairDimension = (dimension) => Object.hasOwn(PAIR_DIMENSIONS, dimension);
/** A pair with an address in it has no hourly rows, as `ip` has none. */
export const pairHasAddress = (dimension) => PAIR_DIMENSIONS[dimension].includes('ip');
/** `path_status` keeps its free-text part first, so it splits at the last space; the rest at the first. */
const splitsAtLast = (dimension) => dimension === 'path_status';
export const joinPairKey = (first, second) => `${first} ${second}`;
export function splitPairKey(dimension, key) {
    const at = splitsAtLast(dimension) ? key.lastIndexOf(' ') : key.indexOf(' ');
    if (at <= 0 || at >= key.length - 1)
        return undefined;
    return [key.slice(0, at), key.slice(at + 1)];
}
const BOT_PART = /^(fake:)?[a-z0-9][a-z0-9_-]{0,63}$/;
/** Whether one half of a pair is what it should be. */
export function validPairPart(part, value) {
    switch (part) {
        case 'ip':
            return parseAddress(value) !== undefined;
        case 'status':
            return /^[1-5][0-9]{2}$/.test(value);
        case 'bot':
            return BOT_PART.test(value);
        case 'path':
            return PRINTABLE.test(value);
    }
}
export const isSiteDimension = (dimension) => Object.hasOwn(SITE_DIMENSIONS, dimension);
/** A host as the log names it: lower case, a name or an address, no spaces. */
export const TrafficHost = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9._:[\]-]+$/, 'a host name');
export const STATUS_CLASSES = ['1xx', '2xx', '3xx', '4xx', '5xx'];
/** Paths, hosts and families are other people's text: printable ASCII, and short. */
const PRINTABLE = /^[\x20-\x7E]+$/;
export const MAX_TRAFFIC_KEY_LENGTH = 256;
const TrafficKey = z.string().min(1).max(MAX_TRAFFIC_KEY_LENGTH).regex(PRINTABLE, 'printable');
export const TrafficVerification = z.enum(['verified', 'failed', 'unknown', 'pending']);
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const TrafficRow = z
    .object({
    dimension: TrafficDimension,
    key: TrafficKey,
    /** On `site_*` rows (P11.7): the site the row belongs to. */
    host: TrafficHost.optional(),
    /** On `bot` rows: the bot's category. */
    botCategory: TrafficCategory.optional(),
    /**
     * On `bot` rows of a bot the catalog can check (P6.4 does). `pending` until
     * then, so a verified badge never appears for a check nobody made.
     */
    verification: TrafficVerification.optional(),
    requests: Count.min(1),
    bytes: Count,
    status4xx: Count,
    status5xx: Count,
    avgResponseMs: z.number().nonnegative().max(3_600_000),
})
    .strict()
    .superRefine((row, ctx) => {
    const fail = (message) => {
        ctx.addIssue({ code: 'custom', path: ['key'], message });
    };
    if (isSiteDimension(row.dimension) !== (row.host !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['host'],
            message: 'a site row names its host, and no other row does',
        });
    }
    if (isPairDimension(row.dimension)) {
        const halves = splitPairKey(row.dimension, row.key);
        const [firstPart, secondPart] = PAIR_DIMENSIONS[row.dimension];
        if (halves === undefined ||
            !validPairPart(firstPart, halves[0]) ||
            !validPairPart(secondPart, halves[1])) {
            fail(`not a ${firstPart} and a ${secondPart}`);
        }
    }
    switch (isSiteDimension(row.dimension) ? SITE_DIMENSIONS[row.dimension] : row.dimension) {
        case 'total':
            if (row.key !== 'all')
                fail('the total row is keyed "all"');
            break;
        case 'category':
            if (!TrafficCategory.safeParse(row.key).success)
                fail('not a traffic category');
            break;
        case 'status':
            if (!STATUS_CLASSES.includes(row.key))
                fail('not a status class');
            break;
        case 'ip':
            if (parseAddress(row.key) === undefined)
                fail('not an address');
            break;
        case 'host':
            if (!/^[a-z0-9._:[\]-]+$/.test(row.key))
                fail('not a host name');
            break;
        default:
            break;
    }
    if (row.status4xx + row.status5xx > row.requests) {
        ctx.addIssue({ code: 'custom', message: 'more errors than requests' });
    }
});
/** `POST /v1/ingest/traffic`. */
export const TrafficBatch = z
    .object({
    /** The agent's own, so a batch sent twice is stored once (rule 7). */
    id: z.uuidv7(),
    /** The minute the requests were read in, on the minute. */
    bucketStart: IsoTime.refine((at) => Date.parse(at) % 60_000 === 0, 'on the minute'),
    bucketSeconds: z.literal(60),
    rows: z.array(TrafficRow).max(2000),
})
    .strict()
    .superRefine((batch, ctx) => {
    const seen = new Set();
    const counts = new Map();
    const hosts = new Set();
    for (const [index, row] of batch.rows.entries()) {
        const id = `${row.dimension}\u0000${row.host ?? ''}\u0000${row.key}`;
        if (seen.has(id)) {
            ctx.addIssue({ code: 'custom', path: ['rows', index], message: 'the same key twice' });
        }
        seen.add(id);
        if (row.host !== undefined)
            hosts.add(row.host);
        const bucket = `${row.dimension}\u0000${row.host ?? ''}`;
        const count = (counts.get(bucket) ?? 0) + 1;
        counts.set(bucket, count);
        if (count > TRAFFIC_CAPS[row.dimension]) {
            ctx.addIssue({
                code: 'custom',
                path: ['rows', index],
                message: `more ${row.dimension} rows than ${String(TRAFFIC_CAPS[row.dimension])}`,
            });
        }
    }
    if (hosts.size > TRAFFIC_SITES) {
        ctx.addIssue({
            code: 'custom',
            path: ['rows'],
            message: `more than ${String(TRAFFIC_SITES)} sites`,
        });
    }
});
export const TrafficReceipt = z.object({
    /** False when the API already had this batch. */
    stored: z.boolean(),
    rows: z.number().int().nonnegative(),
});
/**
 * What the agent's `web_traffic` module is handed (`moduleSlices`): its settings,
 * the confirmed environment and Cloudflare's ranges to check each request's
 * connecting peer against, and the API's bot catalog when the document carries
 * one — newer than the agent's own after an API upgrade (section 8.7).
 */
export const WebTrafficModuleSettings = WebTrafficSettings.extend({
    cloudflare: z.array(AllowlistRange).max(500).default([]),
    /** Crawler operators' published lists by URL (`ConfigRanges.crawlers`; P6.4). */
    crawler_ranges: z.record(z.string(), z.array(AllowlistRange).max(5000)).default({}),
    environment: WebEnvironmentSetting.optional(),
    bot_catalog: BotCatalog.optional(),
});
/*
 * Reading traffic back (knowledge doc sections 8.7, 15 and 16; P6.5): the
 * Traffic page's chart and top lists, and the verification results behind its
 * badges.
 *
 * Minute rows are kept 14 days and carry client addresses; the API rolls them up
 * into hourly rows — every dimension but `ip`, the top 100 keys of each — kept a
 * year. A read comes from minute rows when its range is recent and at most two
 * days long, from hourly rows otherwise (topped up with the minute rows of hours
 * not rolled up yet), and says which it used.
 */
/** How long minute rows (and so every address) are kept (section 16). */
export const TRAFFIC_MINUTE_RETENTION_DAYS = 14;
/** How long hourly rows are kept (section 16). */
export const TRAFFIC_HOURLY_RETENTION_DAYS = 366;
/** Keys per server, dimension and hour the rollup keeps. */
export const TRAFFIC_HOURLY_TOP = 100;
/** The longest range read from minute rows; a longer one reads hourly rows. */
export const TRAFFIC_MINUTE_RANGE_MAX_HOURS = 48;
export const TRAFFIC_RANGE_DEFAULT_HOURS = 24;
/** Buckets per chart. The API widens the bucket rather than cutting the range. */
export const TRAFFIC_POINTS_MAX = 500;
export const TRAFFIC_TOP_MAX = 50;
export const TRAFFIC_TOP_DEFAULT = 10;
export const TrafficSource = z.enum(['minute', 'hour']);
/**
 * Whose traffic: one server, one group's servers, or — with neither — the fleet.
 * Both at once is refused rather than guessed at.
 */
const TrafficScope = z.object({
    server_id: ServerId.optional(),
    group_id: ServerGroupId.optional(),
    /** Inclusive; `to` is exclusive. Defaults to the last day, ending now. */
    from: IsoTime.optional(),
    to: IsoTime.optional(),
});
function checkScope(query, ctx) {
    if (query.server_id !== undefined && query.group_id !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['group_id'],
            message: 'one of `server_id` and `group_id`, not both',
        });
    }
    if (query.from !== undefined &&
        query.to !== undefined &&
        Date.parse(query.from) >= Date.parse(query.to)) {
        ctx.addIssue({ code: 'custom', path: ['from'], message: '`from` must be before `to`' });
    }
}
/** `GET /v1/traffic`. */
export const TrafficSeriesQuery = TrafficScope.superRefine(checkScope);
const Total = z.number().int().nonnegative();
/** One bucket of the chart: every request, and the same requests by category. */
export const TrafficPoint = z.object({
    /** The bucket's start. Buckets with no requests are left out. */
    at: IsoTime,
    requests: Total,
    bytes: Total,
    status4xx: Total,
    status5xx: Total,
    categories: z.partialRecord(TrafficCategory, Total),
});
export const TrafficSeriesResponse = z.object({
    /** The range actually read, after the defaults and the retention bounds. */
    from: IsoTime,
    to: IsoTime,
    source: TrafficSource,
    bucketSeconds: z.number().int().positive(),
    points: z.array(TrafficPoint).max(TRAFFIC_POINTS_MAX + 1),
});
/** Everything but `total`, which is the chart's. */
export const TrafficTopDimension = TrafficDimension.exclude([
    'total',
    'ip_path',
    'path_status',
    'ip_status',
    'bot_path',
    'bot_status',
    'ip_bot',
]);
/** `GET /v1/traffic/top`. */
export const TrafficTopQuery = TrafficScope.extend({
    dimension: TrafficTopDimension,
    limit: z.coerce.number().int().min(1).max(TRAFFIC_TOP_MAX).default(TRAFFIC_TOP_DEFAULT),
    /** The site a `site_*` dimension is read for (P11.7); required with one, refused without. */
    host: TrafficHost.optional(),
}).superRefine((query, ctx) => {
    checkScope(query, ctx);
    if (isSiteDimension(query.dimension) !== (query.host !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['host'],
            message: 'a site dimension is read for one host, and only a site dimension is',
        });
    }
});
export const TrafficTopRow = z.object({
    key: z.string().min(1).max(MAX_TRAFFIC_KEY_LENGTH),
    requests: Total,
    bytes: Total,
    status4xx: Total,
    status5xx: Total,
    avgResponseMs: z.number().nonnegative(),
    botCategory: TrafficCategory.optional(),
    /**
     * Over the whole range: `verified` only when every row was, `failed` for a
     * `fake:` row, else `unknown` if any check could not finish, else `pending`.
     */
    verification: TrafficVerification.optional(),
    /** For an address row: where it is and whose network (DB-IP, approximate). */
    geo: GeoInfo.optional(),
});
export const TrafficTopResponse = z.object({
    dimension: TrafficTopDimension,
    from: IsoTime,
    to: IsoTime,
    source: TrafficSource,
    rows: z.array(TrafficTopRow).max(TRAFFIC_TOP_MAX),
});
/**
 * `GET /v1/traffic/related` (P11.13): what is joined to one row of a top list —
 * the addresses that asked for a page, the pages an address asked for, the codes a
 * bot was answered with. `by` is the row you hold and `show` is what you want
 * beside it; the two must be a pair the agents send (`PAIR_DIMENSIONS`).
 */
export const TrafficRelatedBy = z.enum(['ip', 'path', 'status', 'bot']);
export const TRAFFIC_RELATED_MAX = 50;
export const TrafficRelatedQuery = TrafficScope.extend({
    by: TrafficRelatedBy,
    /** The row's key: an address, a path, a bot id, an exact code (`404`) or a class (`4xx`). */
    key: TrafficKey,
    show: TrafficRelatedBy,
    limit: z.coerce.number().int().min(1).max(TRAFFIC_RELATED_MAX).default(10),
}).superRefine((query, ctx) => {
    checkScope(query, ctx);
    if (pairOf(query.by, query.show) === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['show'],
            message: `the agents do not send ${query.by} joined to ${query.show}`,
        });
    }
    if (query.by === 'status' && !/^([1-5][0-9]{2}|[1-5]xx)$/.test(query.key)) {
        ctx.addIssue({ code: 'custom', path: ['key'], message: 'a status code or class' });
    }
    if (query.by === 'ip' && parseAddress(query.key) === undefined) {
        ctx.addIssue({ code: 'custom', path: ['key'], message: 'not an address' });
    }
});
/** The pair that joins two kinds of row, and which side is which; undefined when none does. */
export function pairOf(by, show) {
    for (const dimension of Object.keys(PAIR_DIMENSIONS)) {
        const [first, second] = PAIR_DIMENSIONS[dimension];
        if (first === by && second === show)
            return { dimension, byIsFirst: true };
        if (second === by && first === show)
            return { dimension, byIsFirst: false };
    }
    return undefined;
}
export const TrafficRelatedRow = TrafficTopRow.pick({
    key: true,
    requests: true,
    bytes: true,
    status4xx: true,
    status5xx: true,
    avgResponseMs: true,
    geo: true,
});
export const TrafficRelatedResponse = z.object({
    by: TrafficRelatedBy,
    key: z.string().max(MAX_TRAFFIC_KEY_LENGTH),
    show: TrafficRelatedBy,
    from: IsoTime,
    to: IsoTime,
    source: TrafficSource,
    rows: z.array(TrafficRelatedRow).max(TRAFFIC_RELATED_MAX),
});
export const BOT_VERIFICATION_PAGE_MAX = 100;
export const BOT_VERIFICATION_PAGE_DEFAULT = 50;
/**
 * `GET /v1/bots/verifications`: checked claims, newest check first. `result=failed`
 * is the fake-bot address list. A server or group matches the claims its servers
 * reported.
 */
export const BotVerificationListQuery = z
    .object({
    server_id: ServerId.optional(),
    group_id: ServerGroupId.optional(),
    result: BotVerificationResult.optional(),
    claimed_bot: BotId.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(BOT_VERIFICATION_PAGE_MAX)
        .default(BOT_VERIFICATION_PAGE_DEFAULT),
    cursor: z.string().max(512).optional(),
})
    .refine((query) => query.server_id === undefined || query.group_id === undefined, {
    message: 'one of `server_id` and `group_id`, not both',
    path: ['group_id'],
});
export const BotVerificationSummary = z.object({
    ip: IpAddress,
    claimedBot: BotId,
    result: BotVerificationResult,
    method: z.enum(['ip_ranges', 'rdns']),
    hostname: z.string().max(253).nullable(),
    firstSeen: IsoTime,
    lastChecked: IsoTime,
    seenCount: z.number().int().positive(),
    /** The servers that reported it — at most a hundred are kept. */
    serverIds: z.array(ServerId).max(100),
});
//# sourceMappingURL=traffic.js.map